import { useState, useEffect } from 'react';
import { projectsApi, personasApi, marketAnalysesApi } from '@/utils/api-pb';
import type { Project, Persona, MarketAnalysis } from '@/utils/pocketbase';

export interface LoadDataResult {
  projectsList: Project[];
  personasList: Persona[];
}

export interface UsePersonaDataReturn {
  projects: Project[];
  personas: Persona[];
  marketAnalyses: MarketAnalysis[];
  isInitialLoading: boolean;
  error: string;
  setError: (error: string) => void;
  loadData: () => Promise<LoadDataResult>;
  loadMarketAnalyses: (projectId: string) => Promise<void>;
  setMarketAnalyses: React.Dispatch<React.SetStateAction<MarketAnalysis[]>>;
}

export function usePersonaData(
  onInitialLoad?: (result: LoadDataResult) => void
): UsePersonaDataReturn {
  const [projects, setProjects] = useState<Project[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [marketAnalyses, setMarketAnalyses] = useState<MarketAnalysis[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = async (): Promise<LoadDataResult> => {
    try {
      const [projectsList, personasList] = await Promise.all([
        projectsApi.list(),
        personasApi.list(),
      ]);
      setProjects(projectsList);
      setPersonas(personasList);

      return { projectsList, personasList };
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Ошибка загрузки данных');
      return { projectsList: [], personasList: [] };
    } finally {
      setIsInitialLoading(false);
    }
  };

  const loadMarketAnalyses = async (projectId: string) => {
    try {
      const list = await marketAnalysesApi.list(projectId);
      setMarketAnalyses(list);
    } catch (err) {
      console.error('Error loading market analyses:', err);
    }
  };

  useEffect(() => {
    loadData().then((result) => {
      if (onInitialLoad) {
        onInitialLoad(result);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    projects,
    personas,
    marketAnalyses,
    isInitialLoading,
    error,
    setError,
    loadData,
    loadMarketAnalyses,
    setMarketAnalyses,
  };
}
